import React, { type ReactElement, useState, useMemo, useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { type InferType, object, ref, string } from 'yup'
import { useFormik } from 'formik'
import { CircularProgress, IconButton, InputAdornment, Typography } from '@mui/material'
import { ReactComponent as EyeCrossed } from 'assets/eyeCrossed.svg'
import backgroundGraphics from 'assets/backgroundGraphics.svg'
import { ReactComponent as Favicon } from 'assets/favicon.svg'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import LabeledTextfield from 'common/ui/labeled-textfield'
import { ReactComponent as Eye } from 'assets/eye.svg'
import Message from 'common/authentication/Message'
import ThemeButton from 'common/ui/theme-button'
import { StoreStatus } from 'common/utils/types'

import { resetPassword, signIn } from '../auth.service'
import { resetStatus } from '../auth.slice'

import styles from './ResetPassword.module.scss'

const resetPasswordSchema = object({
  email: string().email().required().default(''),
  password: string()
    .required('Obavezno polje')
    .default('')
    .min(
      8,
      'Lozinka mora sadržati minimum 8 karaktera, jedno veliko slovo, jedno malo slovo, broj i specijalni karakter',
    )
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[~!@#$%^&*_\-+=`|\\(){}[\]:;"'<>,.?/]).*$/,
      'Lozinka mora sadržati minimum 8 karaktera, jedno veliko slovo, jedno malo slovo, broj i specijalni karakter',
    ),
  confirmPassword: string()
    .required('Obavezno polje')
    .default('')
    .oneOf([ref('password')], 'Lozinke moraju biti iste'),
})

type FormValues = InferType<typeof resetPasswordSchema>
const ResetPassword: React.FC = (): ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const { user, resetPasswordStatus, status } = useAppSelector((state) => state.auth)
  const email: string = useMemo(
    () => ((location.state as Record<string, string>)?.email || searchParams.get('email')) ?? '',
    [],
  )

  const defaultValues: FormValues = {
    email,
    password: '',
    confirmPassword: '',
  }

  useEffect(() => {
    if (!email) {
      navigate('/', { replace: true })
    }
  }, [])

  useEffect(() => {
    if (resetPasswordStatus === StoreStatus.Succeeded) {
      const { email, password } = formik.values
      void dispatch(signIn({ email, password }))
      dispatch(resetStatus())
    }
  }, [dispatch, navigate, resetPasswordStatus])

  const formik = useFormik({
    initialValues: defaultValues,
    onSubmit: async (values: FormValues) => {
      await dispatch(resetPassword({ user, password: values.password }))
    },
    validationSchema: resetPasswordSchema,
  })

  return (
    <div
      className={styles.container}
      style={{
        backgroundImage: `url(${backgroundGraphics})`,
        backgroundPosition: 'left',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto 100%',
      }}
    >
      <div className={styles.textContainer}>
        <p className={styles.text}>
          Dobrodošli na <b>CDT Vendors</b> platformu
        </p>
      </div>
      <form onSubmit={formik.handleSubmit} className={styles.cardBig}>
        <Message />
        <div className={styles.filterCard}>
          <div className={styles.header}>
            <Favicon style={{ height: '20px', width: '20px' }} />
            <Typography variant="title1" sx={{ lineHeight: '22px' }}>
              Kreirajte lozinku
            </Typography>
          </div>
          <div className={styles.border} />
          <div className={styles.filtersContainer}>
            <LabeledTextfield
              label="*Lozinka"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && !!formik.errors.password}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                classes: {
                  input: styles.passwordField, // Apply custom CSS class to input element
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowPassword((show) => !show)
                        document.getElementById('password')?.focus()
                      }}
                      edge="end"
                    >
                      {showPassword ? <Eye /> : <EyeCrossed />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <LabeledTextfield
              label="*Ponovite lozinku"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              id="confirmPassword"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            />
          </div>
          <div className={styles.border} />
          <div className={styles.footer}>
            <ThemeButton
              onClick={() => {
                void formik.submitForm()
              }}
              type="submit"
              disabled={!user || !formik.isValid || !formik.dirty || status === StoreStatus.Loading}
              startIcon={
                status === StoreStatus.Loading ? <CircularProgress size={16} color="primary" /> : null
              }
            >
              Kreiraj lozinku
            </ThemeButton>
          </div>
        </div>
      </form>
    </div>
  )
}

export default ResetPassword
